/**
 * Theme system — System / Light / Dark with localStorage persistence.
 * The .dark class on <html> drives all styles (see app.css).
 */

import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

export type Theme = "system" | "light" | "dark";

interface ThemeCtx {
  theme: Theme;
  effective: "light" | "dark";
  setTheme: (t: Theme) => void;
  cycle: () => void;
}

const Ctx = createContext<ThemeCtx | null>(null);

function readSystem(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(theme: Theme) {
  const effective = theme === "system" ? readSystem() : theme;
  document.documentElement.classList.toggle("dark", effective === "dark");
  return effective;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      return (localStorage.getItem("keep:theme") as Theme) || "system";
    } catch {
      return "system";
    }
  });
  const [effective, setEffective] = useState<"light" | "dark">(() => applyTheme(theme));

  useEffect(() => {
    setEffective(applyTheme(theme));
    try {
      localStorage.setItem("keep:theme", theme);
    } catch {}
  }, [theme]);

  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => setEffective(applyTheme("system"));
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  const setTheme = (t: Theme) => setThemeState(t);
  const cycle = () =>
    setThemeState((t) => (t === "system" ? "light" : t === "light" ? "dark" : "system"));

  return <Ctx.Provider value={{ theme, effective, setTheme, cycle }}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("ThemeProvider missing");
  return c;
}
